/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { Observable, zip, EMPTY, of, iif } from 'rxjs';
import { map, switchMap, tap, catchError, mergeMap, take } from 'rxjs/operators';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import * as globalActions from '@app/store/actions/global.actions';
import * as alertActions from '@user-alerts/store/actions/user-alerts.actions';
import { environment } from '@environment';
import { UserService } from '@app/modules/core/services/user.service';
import { AirportsService } from '@app/modules/core/services/airports.service';
import { PlacesService } from '@app/modules/core/services/places.service';
import { AirlinesService } from '@app/modules/core/services/airlines.service';
import { NewAddress } from '@app/modules/core/interfaces/address';
import { setUserId, triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { BusCompaniesService } from '@app/modules/core/services/bus-companies.service';
import { BusTerminalsService } from '@app/modules/core/services/bus-terminals.service';
import { AuthService } from '@app/services/auth.service';
import { getDefaultCityByCountry } from '@app/modules/core/mappers/default-city-by-country.mapper';
import { FrequentFlyerProgramsService } from '@app/services/frequent-flyer-programs.service';
import { ProvincesAndCitiesExternalService } from '@app/services/provinces-cities-external.service';
import { getProvincesByCity } from '@app/mappers/provinces-by-city.mapper';
import { Place } from '@app/entities/place';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { ProvincesAndCitiesExternal } from '@app/entities/provinces-cities';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { AMPLITUDE_EVENTS } from '@app/modules/core/constants/amplitude.constants';
import { fetchStatsigExperimentsSuccess } from '@statsig/store/statsig.actions';
import { TriggerAmplitudeEntity } from '@amplitude/interfaces/trigger-entity';
import { SessionStorageService } from '@app/services/session-storage.service';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import { User, UserInfo } from '@app/modules/core/interfaces/user';
import { setBranchCodeAction } from '@shared/base-search-bar/store/actions/search-bar.actions';
import { fetchHomeDataSuccessAction } from '@home/store/actions/home.actions';
import { LoginType } from '@app/entities/login-type';
import { isEmpty, isNull } from '@app/util/util';
import { BenefitsCookie } from '@app/constants/cookies';
import { decodeURIParams } from '@app/mappers/encoding.mapper';
import { StatsigExperimentsConfig } from '@statsig/statsig.entities';

@Injectable()
export class BootstrapEffects {

  constructor(
    private readonly action$: Actions,
    private readonly service: UserService,
    private readonly airportsService: AirportsService,
    private readonly airlinesService: AirlinesService,
    private readonly placesServices: PlacesService,
    private readonly busCompaniesServices: BusCompaniesService,
    private readonly busTerminalsService: BusTerminalsService,
    private readonly frequentFlyerProgramsService: FrequentFlyerProgramsService,
    private readonly provincesAndCitiesExternalService: ProvincesAndCitiesExternalService,
    private readonly authService: AuthService,
    private readonly cookieService: CookieService,
    private readonly triggerAmplitudeService: TriggerAmplitudeService,
    private readonly router: Router,
    private readonly sessionStorageService: SessionStorageService,
    private readonly headerService: HeaderService,
  ) { }

  initAssets$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.initAssetsAction),
      switchMap(() => zip(
        this.placesServices.fetchPlaces(),
        this.busCompaniesServices.fetchBusCompanies(),
        this.busTerminalsService.fetchBusTerminals(),
        this.airportsService.fetchAirports(),
        this.airlinesService.fetchAirlines(),
        this.frequentFlyerProgramsService.frequentFlyerPrograms(),
        this.provincesAndCitiesExternalService.fetchProvincesAndCitiesRelation()
      ).pipe(
        map((
          assets: [Place[], string[], string[], string[], string[], 
          FrequentFlyerResponse[], ProvincesAndCitiesExternal]) => ({
          places: assets[0], busCompanies: assets[1], busTerminals: assets[2], airports: assets[3],
          airlines: assets[4], programs: assets[5], provincesRelation: assets[6]
        })),
        switchMap(({ places, busCompanies, busTerminals, airports, airlines, programs, provincesRelation }) => [
          globalActions.fetchPlacesSuccessAction({ places }),
          globalActions.fetchBusCompaniesSuccessAction({ busCompanies }),
          globalActions.fetchBusTerminalsSuccessAction({ busTerminals }),
          globalActions.fetchAirportsSuccessAction({ airports }),
          globalActions.fetchAirlinesSuccessAction({ airlines }),
          globalActions.fetchFrequentFlyerProgramsSuccessAction({ programs }),
          globalActions.fetchProvincesAndCitiesExternalSuccessAction({ provinces: getProvincesByCity(provincesRelation) }),
          globalActions.fetchStaticListsAction()
        ])
      )),
    ));

  loadUserDataFromInit$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.loadUserDataFromInitAction),
      switchMap(payload => {
        const { user, userInfo, experiments } = payload;
        const returnActions = [];
        if (user) {
          setUserId(user.id);
        }
        returnActions.push(globalActions.fetchUserAlertsAction({ user }));
        returnActions.push(globalActions.loadUserDataSuccessAction({ user }));
        returnActions.push(globalActions.loadUserInfoSuccessAction({ userInfo }));
        returnActions.push(globalActions.loadPageDataAction({ user, userInfo, experiments, isLoggedIn: true }));
        returnActions.push(fetchStatsigExperimentsSuccess({ experiments }));
        return returnActions;
      })
    )
  );

  loadPageDate$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.loadPageDataAction),
      switchMap(payload => {
        const { user, userInfo, experiments, isLoggedIn } = payload;
        return this.fetchPageData$(user, userInfo, isLoggedIn, experiments);
      }),
    )
  );

  fetchUserAlerts$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.loadUserDataSuccessAction, alertActions.reloadUserAlertsAction),
      switchMap(action => this.service.fetchUserAlerts(action.user.id)
        .pipe(
          mergeMap(response => {
            return [
              globalActions.fetchUserAlertSuccessAction({ alerts: response })
            ];
          }),
          catchError(() => EMPTY)
        )
      )
    ));

  fetchUserAddresses$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.loadUserDataSuccessAction),
      switchMap(() => this.service.getUserAddresses()
        .pipe(
          mergeMap(response => {
            if (response.addresses.length) {
              const address = response.addresses.find(item => !!item.active);
              if (address) {
                return [
                  globalActions.setDefaultAddressAction({ address }),
                  globalActions.loadUserAddressesSuccessAction({ addresses: response }),
                ];
              } else {
                const activeAddress = { ...response.addresses[0], active: true };
                return [
                  globalActions.activeDefaultAddressesAction({ address: activeAddress }),
                  globalActions.loadUserAddressesSuccessAction({
                    addresses: {
                      addresses: response.addresses.map((current, i) => i === 0 ? activeAddress : {
                        ...current, active: false
                      })
                    }
                  }),
                ];
              }
            } else return [globalActions.createDefaultAddressesAction()];
          }),
          catchError(() => EMPTY)
        )
      )
    ));

  fetchStaticLists$: Observable<Action> = createEffect(() => this.action$.pipe(
    ofType(globalActions.fetchStaticListsAction),
    switchMap(() => this.service.fetchStaticLists().pipe(
      map(staticLists => globalActions.fetchStaticListsSuccessAction({ staticLists })),
      catchError(error => of(globalActions.fetchStaticListsFailureAction({ error })))
    ))
  ));

  createDefaultAddress$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.createDefaultAddressesAction),
      switchMap(() => this.service.getCountryCities().pipe(
        map(cities => getDefaultCityByCountry(cities)),
        switchMap(cityByCountry => {
          const defaultCity: NewAddress = cityByCountry ? {
            ...environment.default_address, city: cityByCountry.id
          } : environment.default_address as NewAddress;
          return this.service.createUserAddress(defaultCity)
            .pipe(switchMap(response => [globalActions.createDefaultAddressesSuccessAction({ response })]));
        })
      ))
    )
  );

  setHistoryToSessionStorageAction$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.setHistoryToSessionStorageAction),
      map((action) => {
        this.sessionStorageService.setHistory(action.payload);
      }),
      map(() => globalActions.setHistoryToSessionStorageSuccessAction())
    ));

  restoreHistoryAction$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.restoreHistoryAction),
      map((action) => {
        const history = this.sessionStorageService.getHistory();
        return globalActions.restoreHistorySuccessAction({ payload: history });
      }),
    ));

  activeDefaultAddresses$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.activeDefaultAddressesAction),
      switchMap(action => this.service.activeUserAddresses(action.address)),
      map(response => globalActions.activeDefaultAddressesSuccessAction({ response }))
    )
  );

  removeTokenAction$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.removeTokenAction),
      tap(() => {
        this.authService.removeToken();
        this.cookieService.delete('SeamlessLogin');
      }),
      map(() => globalActions.removeTokenSuccessAction())
    ));

  loggerBookingAction$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.loggerBookingAction)
    ), { dispatch: false });

  authenticateTokenAction$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(globalActions.authenticateTokenAction),
      switchMap(action => this.service.authseamlessLoginToken(action.payload)
        .pipe(
          map(response => globalActions.fetchSeamlessLoginTokenSuccessAction({ response: response.Token.TokenId })),
          catchError((error) => {
            return this.registerAmplitudeSeamlessAuthError$(error).pipe(
              tap(trigger => triggerAmplitudeEventApp(trigger)),
              map(() => {
                this.router.navigate(['/']);
                return globalActions.removeTokenAction();
              })
            );
          })
        )
      )
    ));


  private registerAmplitudeSeamlessAuthError$(payload: HttpErrorResponse): Observable<TriggerAmplitudeEntity> {
    return this.triggerAmplitudeService.trackAmplitudeEventProps$(AMPLITUDE_EVENTS.TRAVEL_SEAMLESS_AUTH_ERROR, {
      ERROR_MESSAGE: payload?.error?.message || payload?.message || payload?.error
    }).pipe(take(1));
  }

  private fetchPageData$(
    user: User,
    userInfo: UserInfo,
    isLoggedIn: boolean,
    experiments: StatsigExperimentsConfig[],
  ): Observable<Action> {
    const isPrime = userInfo?.is_prime || false;
    const loyalty = user?.loyalty?.type || null;
    const userId = user?.id || null;
    const treatments = [];
    const levelPrime = userInfo?.level_prime|| null;
    const benefitsCookie = this.cookieService.get(BenefitsCookie);
    if (experiments) {
      experiments.forEach(e => treatments.push({ split_name: e.name, treatment: e.treatment.toString() }));
    }
    return zip(
      this.service.fetchPageData({
        userId, isPrime, loyalty, treatments: JSON.stringify(treatments), levelPrime
      }).pipe(
        map(pageData => ({ pageData, pageDataError: null })),
        catchError((error) => of({ pageData: null, pageDataError: error }))
      ),
      iif(
        () => isEmpty(benefitsCookie),
        this.service.getUserBenefits({
          logged: isLoggedIn, is_prime: isPrime, treatments, product_references: [],
        }).pipe(
          map(benefits => ({ benefits, benefitsError: null })),
          catchError((error) => of({ benefits: null, benefitsError: error }))
        ),
        of({ benefits: JSON.parse(decodeURIParams(benefitsCookie)), benefitsError: null })
      )
    ).pipe(
      switchMap(([{ pageData, pageDataError }, { benefits }]) => {
        if (!isNull(pageDataError)) {
          if (pageDataError?.status === 401) {
            return [
              globalActions.removeTokenAction(),
              globalActions.loadPageDataAction({ user: null, userInfo: null, experiments, isLoggedIn: false })
            ];
          } else {
            return [];
          }
        } else if (!pageData.login_required || isLoggedIn) {
          return [
            setBranchCodeAction({ branchCode: pageData.branch_code }),
            fetchHomeDataSuccessAction({ response: pageData }),
            globalActions.loadUserBenefitsSuccessAction({ benefits: benefits })
          ];
        } else {
          this.headerService.forceUserLogin(LoginType.Anywhere);
          return [];
        }
      }),
    )
  }
}
